import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CopyToClipboard as ReactCopyToClipboard } from 'react-copy-to-clipboard';
import { SystemIcon, tokens, Typography } from '@utmost.co/utmost-design-system';

interface PermissionHelperProps {
  resourceUid?: string;
}

export const CopyPermissionResource: React.FC<React.PropsWithChildren<PermissionHelperProps>> = ({ resourceUid }) => {
  const [copied, setCopied] = useState(false);

  const onCopyHandler = () => setCopied(true);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (copied) {
      timeout = setTimeout(() => setCopied(false), 1200);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [copied]);

  if (!resourceUid) {
    return (
      <StyledCopyContainer>
        <StyledTypography $disabled={!resourceUid} variant="body2">
          No associated resource uid
        </StyledTypography>
      </StyledCopyContainer>
    );
  }

  return (
    <ReactCopyToClipboard text={resourceUid} onCopy={onCopyHandler}>
      <StyledCopyContainer>
        <StyledTypography variant="body2"> {resourceUid}</StyledTypography>
        <StyledSystemIcon
          size={16}
          color={copied ? tokens.colorSuccess : tokens.colorOnSurfaceVariant}
          className="copy-icon"
          name={copied ? 'check_circle' : 'file_copy'}
          title={copied ? 'Copied!' : 'Copy to clipboard'}
        />
      </StyledCopyContainer>
    </ReactCopyToClipboard>
  );
};

const StyledCopyContainer = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  cursor: pointer;
  gap: 6px;
  position: relative;

  &:hover .copy-icon {
    visibility: visible;
    opacity: 1;
  }

  .copy-icon {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }
`;

const StyledTypography = styled(Typography)<{ $disabled?: boolean }>`
  &&:hover {
    text-decoration: ${($disabled) => ($disabled ? 'none' : 'underline')};
  }
`;

const StyledSystemIcon = styled(SystemIcon)`
  display: block;
`;
